import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  UserX, Plus, X, Search, MessageCircle, Pencil, Trash2, BarChart3,
  ListChecks, Settings, Send, Clock,
} from "lucide-react";
import { MonthPicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { useDebounce } from "@/hooks/useDebounce";
import { formatTanggal, formatTanggalPanjang, jamSekarangJakarta, hariIniISO } from "@/lib/tanggal";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";

/** Jam WIB yang benar-benar berdetak — usher melihat waktu kunjungannya akan
 *  tercatat SEKARANG, bukan angka beku. Nilai yang sungguh disimpan tetap
 *  dari jam SERVER saat POST diterima (lihat backend `datetime.now(WIB)`) —
 *  komponen ini murni tampilan, tidak pernah dikirim ke backend.
 *  Pakai `hariIniISO()` (bukan `toISOString().slice(0,10)` atau bagian
 *  Date() lokal) — keduanya salah tanggal untuk pengguna WIB antara
 *  00:00-07:00 kalau device tidak di-set WIB (lihat CLAUDE.md jebakan zona waktu). */
function JamOtomatis() {
  const [label, setLabel] = useState(
    () => `${formatTanggalPanjang(hariIniISO(), { denganHari: true })}, ${jamSekarangJakarta()} WIB`
  );
  useEffect(() => {
    const t = setInterval(() => {
      setLabel(`${formatTanggalPanjang(hariIniISO(), { denganHari: true })}, ${jamSekarangJakarta()} WIB`);
    }, 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm text-emerald-800">
      <Clock className="w-4 h-4 flex-shrink-0" />
      <span>Waktu kunjungan (otomatis): <span className="font-semibold">{label}</span></span>
    </div>
  );
}

const emptyForm = () => ({
  nama_pelanggan: "", no_hp: "", alasan_kode: "", alasan_catatan: "",
  sales_menangani: "", cabang: "", produk_diminati: "",
});

export default function LossCustomer() {
  const { can } = useAuth();
  const bolehLihat = can("loss_customer", "view");
  const bolehCatat = can("loss_customer", "create");
  const bolehUbah = can("loss_customer", "update");
  const bolehHapus = can("loss_customer", "delete");

  const [alasanOpsi, setAlasanOpsi] = useState([]);
  const [salesOptions, setSalesOptions] = useState([]);
  const [branches, setBranches] = useState([]);

  // ---- Form catat kunjungan (dipakai usher mode & modal manager) ----
  // Inisialisasi lazy berdasarkan kombinasi izin (bukan setState di dalam body
  // render): usher (create tanpa view) langsung melihat form terbuka — itu
  // satu-satunya hal yang bisa mereka lakukan di halaman ini.
  const [form, setForm] = useState(() => (!bolehLihat && bolehCatat) ? emptyForm() : null);
  const [busy, setBusy] = useState(false);
  const [tercatatSesiIni, setTercatatSesiIni] = useState(0);

  // ---- Tab & daftar (hanya untuk pemegang izin "view") ----
  const [tab, setTab] = useState("daftar");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [q, setQ] = useState("");
  const [filterCabang, setFilterCabang] = useState("");
  const [filterAlasan, setFilterAlasan] = useState("");
  const [filterPeriode, setFilterPeriode] = useState("");

  // ---- Rekap ----
  const [rekap, setRekap] = useState(null);
  const [loadingRekap, setLoadingRekap] = useState(false);
  const [rekapPeriode, setRekapPeriode] = useState("");
  const [rekapCabang, setRekapCabang] = useState("");

  // ---- Edit ----
  const [editForm, setEditForm] = useState(null);
  const [editBusy, setEditBusy] = useState(false);

  // ---- Kirim WA ----
  const [waTarget, setWaTarget] = useState(null);
  const [waProduk, setWaProduk] = useState("");
  const [waPesanCustom, setWaPesanCustom] = useState("");
  const [waBusy, setWaBusy] = useState(false);

  // ---- Pengaturan (template pesan + daftar alasan) ----
  const [pengaturan, setPengaturan] = useState(null);
  const [pengaturanBusy, setPengaturanBusy] = useState(false);

  // Daftar alasan untuk dropdown form — dibuka usher (create) MAUPUN manager
  // (view), endpoint gabungan sengaja tidak butuh keduanya sekaligus.
  useEffect(() => {
    if (!bolehLihat && !bolehCatat) return;
    api.get("/loss-customer/alasan-opsi")
      .then(r => setAlasanOpsi(r.data.items || []))
      .catch(() => {});
    // Dropdown ringan, terbuka untuk siapa pun yang login (lihat
    // routers/finance/branches_master.py & routers/users.py::untuk-penugasan)
    // — kalau gagal, kolomnya tetap bisa diketik bebas.
    api.get("/finance/branches/master")
      .then(r => setBranches((r.data.items || []).map(b => b.name)))
      .catch(() => {});
    api.get("/users/untuk-penugasan")
      .then(r => setSalesOptions((r.data.items || []).map(u => u.name).filter(Boolean)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Parameter eksplisit (default ke state terkini) — pola sama dengan
  // WaitingList.jsx: effect di bawah memanggil dengan `qDebounced` secara
  // eksplisit, supaya query yang benar-benar ditembak jelas sama dengan yang
  // ada di dependency array (bukan diam-diam membaca `q` mentah lewat closure).
  const loadList = async (cari = q, cabang = filterCabang, alasan = filterAlasan, periode = filterPeriode) => {
    setLoadingList(true);
    try {
      const params = {};
      if (cari.trim()) params.q = cari.trim();
      if (cabang) params.cabang = cabang;
      if (alasan) params.alasan_kode = alasan;
      if (periode) params.periode = periode;
      const r = await api.get("/loss-customer", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data Loss Customer");
    } finally { setLoadingList(false); }
  };

  const qDebounced = useDebounce(q, 400);
  useEffect(() => {
    if (!bolehLihat || tab !== "daftar") return;
    loadList(qDebounced, filterCabang, filterAlasan, filterPeriode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bolehLihat, tab, qDebounced, filterCabang, filterAlasan, filterPeriode]);

  const loadRekap = async () => {
    setLoadingRekap(true);
    try {
      const params = {};
      if (rekapPeriode) params.periode = rekapPeriode;
      if (rekapCabang) params.cabang = rekapCabang;
      const r = await api.get("/loss-customer/rekap", { params });
      setRekap(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat rekap");
    } finally { setLoadingRekap(false); }
  };

  useEffect(() => {
    if (!bolehLihat || tab !== "rekap") return;
    loadRekap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bolehLihat, tab, rekapPeriode, rekapCabang]);

  const loadPengaturan = async () => {
    try {
      const r = await api.get("/loss-customer/pengaturan");
      setPengaturan(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat pengaturan");
    }
  };

  useEffect(() => {
    if (!bolehUbah || tab !== "pengaturan") return;
    loadPengaturan();
  }, [bolehUbah, tab]);

  const simpanForm = async () => {
    if (!form.nama_pelanggan.trim()) { toast.error("Nama pelanggan wajib diisi"); return; }
    if (!form.cabang.trim()) { toast.error("Cabang wajib diisi"); return; }
    if (!form.alasan_kode) { toast.error("Pilih alasan belum membeli"); return; }
    if (form.alasan_kode === "lainnya" && !form.alasan_catatan.trim()) {
      toast.error("Alasan 'Lainnya' wajib disertai catatan"); return;
    }
    setBusy(true);
    try {
      const r = await api.post("/loss-customer", form);
      toast.success(r.data.message || "Kunjungan tercatat");
      setTercatatSesiIni(n => n + 1);
      if (bolehLihat) {
        setForm(null);          // manager: tutup modal seperti pola WaitingList
        if (tab === "daftar") loadList();
      } else {
        setForm(emptyForm());   // usher: form dikosongkan lagi, siap untuk pengunjung berikutnya
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setBusy(false); }
  };

  const bukaEdit = (it) => {
    setEditForm({
      id: it.id, nama_pelanggan: it.nama_pelanggan || "", no_hp: it.no_hp || "",
      alasan_kode: it.alasan_kode || "", alasan_catatan: it.alasan_catatan || "",
      sales_menangani: it.sales_menangani || "", cabang: it.cabang || "",
      // Kompatibilitas mundur (poin 8 owner): catatan lama tidak punya field ini
      // sama sekali — `it.produk_diminati` bisa `undefined`, jatuh ke "".
      produk_diminati: it.produk_diminati || "",
    });
  };

  const simpanEdit = async () => {
    if (!editForm) return;
    setEditBusy(true);
    try {
      const { id, ...body } = editForm;
      const r = await api.patch(`/loss-customer/${id}`, body);
      toast.success(r.data.message || "Data diperbarui");
      setEditForm(null);
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan perubahan");
    } finally { setEditBusy(false); }
  };

  const hapus = async (it) => {
    if (!window.confirm(`Hapus data kunjungan ${it.nama_pelanggan}?`)) return;
    try {
      const r = await api.delete(`/loss-customer/${it.id}`);
      toast.success(r.data.message || "Data dihapus");
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus");
    }
  };

  const bukaKirimWa = (it) => {
    setWaTarget(it);
    // Prefill dari produk yang sudah dicatat usher saat kunjungan (poin 8
    // owner) — manager tidak perlu mengetik ulang, tapi tetap bisa diubah.
    setWaProduk(it.produk_diminati || "");
    setWaPesanCustom("");
  };

  const kirimWa = async () => {
    if (!waTarget) return;
    setWaBusy(true);
    try {
      const body = waPesanCustom.trim()
        ? { pesan: waPesanCustom.trim() }
        : { produk: waProduk.trim() };
      const r = await api.post(`/loss-customer/${waTarget.id}/kirim-wa`, body);
      if (r.data.ok) {
        toast.success("WhatsApp terkirim ke " + waTarget.nama_pelanggan);
        setWaTarget(null);
        loadList();
      } else {
        toast.error(r.data.detail || "Gagal mengirim WhatsApp");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim WhatsApp");
    } finally { setWaBusy(false); }
  };

  const simpanPengaturan = async () => {
    if (!pengaturan) return;
    setPengaturanBusy(true);
    try {
      const r = await api.put("/loss-customer/pengaturan", {
        template_pesan: pengaturan.template_pesan,
        alasan_opsi: pengaturan.alasan_opsi,
      });
      toast.success(r.data.message || "Pengaturan disimpan");
      setAlasanOpsi(r.data.alasan_opsi || pengaturan.alasan_opsi || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan pengaturan");
    } finally { setPengaturanBusy(false); }
  };

  const ubahAlasanOpsi = (idx, field, value) => {
    setPengaturan(p => {
      const items = [...p.alasan_opsi];
      items[idx] = { ...items[idx], [field]: value };
      return { ...p, alasan_opsi: items };
    });
  };

  const tambahAlasanOpsi = () => {
    setPengaturan(p => ({ ...p, alasan_opsi: [...p.alasan_opsi, { kode: "", label: "" }] }));
  };

  const hapusAlasanOpsi = (idx) => {
    setPengaturan(p => ({ ...p, alasan_opsi: p.alasan_opsi.filter((_, i) => i !== idx) }));
  };

  // ======== Form fields (dipakai form utama & modal edit) ========
  const renderFormFields = (f, setF, prefix) => (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-medium text-slate-600">Nama Pelanggan <span className="text-rose-500">*</span></label>
        <input value={f.nama_pelanggan} data-testid={`${prefix}-nama`}
               placeholder="cth. Budi Santoso"
               onChange={e => setF(v => ({ ...v, nama_pelanggan: e.target.value }))}
               className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">No. HP / WhatsApp</label>
        <input value={f.no_hp} data-testid={`${prefix}-hp`} inputMode="tel"
               placeholder="08xxxxxxxxxx"
               onChange={e => setF(v => ({ ...v, no_hp: e.target.value }))}
               className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">Cabang <span className="text-rose-500">*</span></label>
          <input value={f.cabang} list="lc-branches" data-testid={`${prefix}-cabang`}
                 placeholder="Toko tempat pelanggan datang"
                 onChange={e => setF(v => ({ ...v, cabang: e.target.value }))}
                 className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          <datalist id="lc-branches">
            {branches.map(b => <option key={b} value={b} />)}
          </datalist>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Sales yang Menangani</label>
          <input value={f.sales_menangani} list="lc-sales" data-testid={`${prefix}-sales`}
                 placeholder="Nama sales/frontliner"
                 onChange={e => setF(v => ({ ...v, sales_menangani: e.target.value }))}
                 className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          <datalist id="lc-sales">
            {salesOptions.map(s => <option key={s} value={s} />)}
          </datalist>
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">Produk yang Diincar (opsional)</label>
        <input value={f.produk_diminati || ""} data-testid={`${prefix}-produk`}
               placeholder="cth. iPhone 15 Pro Max 256GB warna Titanium"
               onChange={e => setF(v => ({ ...v, produk_diminati: e.target.value }))}
               className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        <p className="text-[10px] text-slate-400 mt-1">Barang/varian apa yang tadi ditanyakan — dipakai owner untuk rekap "produk paling dicari".</p>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">Alasan Belum Membeli <span className="text-rose-500">*</span></label>
        <select value={f.alasan_kode} data-testid={`${prefix}-alasan`}
                onChange={e => setF(v => ({ ...v, alasan_kode: e.target.value }))}
                className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
          <option value="">-- Pilih alasan --</option>
          {alasanOpsi.map(a => <option key={a.kode} value={a.kode}>{a.label}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">
          Catatan {f.alasan_kode === "lainnya" && <span className="text-rose-500">*</span>}
        </label>
        <textarea value={f.alasan_catatan} rows={2} data-testid={`${prefix}-catatan`}
                  placeholder={f.alasan_kode === "lainnya" ? "Wajib diisi untuk alasan 'Lainnya'" : "Catatan tambahan (opsional)"}
                  onChange={e => setF(v => ({ ...v, alasan_catatan: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
      </div>
    </div>
  );

  // Kedua modal (catat kunjungan & edit) ada di JSX komponen halaman yang
  // tidak unmount — `active` mengikuti state buka masing-masing supaya
  // dirty-state di-reset tiap kali dibuka lagi untuk pelanggan lain. Hook
  // ini dideklarasikan SEBELUM early-return mode usher di bawah supaya
  // urutan hook React tetap konsisten tiap render.
  const formGuard = useModalGuard({ active: !!form, dangerous: true, busy, onClose: () => setForm(null), onSubmit: simpanForm });
  const editGuard = useModalGuard({ active: !!editForm, dangerous: true, busy: editBusy, onClose: () => setEditForm(null), onSubmit: simpanEdit });

  // ======== Mode usher: tidak punya izin "view" — hanya form, tanpa daftar/rekap ========
  if (!bolehLihat && bolehCatat) {
    return (
      <div className="p-4 md:p-8 max-w-xl mx-auto space-y-5" data-testid="loss-customer-usher">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">POS &amp; Sales</p>
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <UserX className="w-7 h-7 text-rose-600" /> Loss Customer
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Catat pengunjung yang keluar toko tanpa membeli. Tercatat sesi ini: <span className="font-semibold">{tercatatSesiIni}</span>
          </p>
        </div>
        <JamOtomatis />
        {form && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            {renderFormFields(form, setForm, "lc-f")}
            <button onClick={simpanForm} disabled={busy} data-testid="lc-f-save"
                    className="mt-4 w-full h-11 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50">
              {busy ? "Menyimpan..." : "Simpan Kunjungan"}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!bolehLihat && !bolehCatat) {
    return (
      <div className="p-8 text-center text-slate-500" data-testid="loss-customer-no-access">
        Anda tidak punya akses ke modul Loss Customer.
      </div>
    );
  }

  // ======== Mode manager/owner: view (+ create/update/delete sesuai izin) ========
  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="loss-customer">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">POS &amp; Sales</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <UserX className="w-7 h-7 text-rose-600" /> Loss Customer
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Pengunjung yang datang tapi keluar tanpa membeli — dipakai untuk tahu KENAPA, bukan menyalahkan siapa.
          </p>
        </div>
        {bolehCatat && (
          <button onClick={() => setForm(emptyForm())} data-testid="lc-add"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Catat Kunjungan
          </button>
        )}
      </div>

      {/* Tab */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit flex-wrap">
        <button onClick={() => setTab("daftar")} data-testid="lc-tab-daftar"
                className={`h-8 px-3 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "daftar" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
          <ListChecks className="w-3.5 h-3.5" /> Daftar
        </button>
        <button onClick={() => setTab("rekap")} data-testid="lc-tab-rekap"
                className={`h-8 px-3 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "rekap" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
          <BarChart3 className="w-3.5 h-3.5" /> Rekap
        </button>
        {bolehUbah && (
          <button onClick={() => setTab("pengaturan")} data-testid="lc-tab-pengaturan"
                  className={`h-8 px-3 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "pengaturan" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
            <Settings className="w-3.5 h-3.5" /> Pengaturan
          </button>
        )}
      </div>

      {/* ===== Tab Daftar ===== */}
      {tab === "daftar" && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={e => setQ(e.target.value)} data-testid="lc-search"
                     placeholder="Cari nama / HP..."
                     className="h-9 w-full pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <input value={filterCabang} onChange={e => setFilterCabang(e.target.value)} list="lc-branches"
                   data-testid="lc-filter-cabang" placeholder="Semua cabang"
                   className="h-9 w-40 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            <select value={filterAlasan} onChange={e => setFilterAlasan(e.target.value)} data-testid="lc-filter-alasan"
                    className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
              <option value="">Semua alasan</option>
              {alasanOpsi.map(a => <option key={a.kode} value={a.kode}>{a.label}</option>)}
            </select>
            <MonthPicker value={filterPeriode} onChange={setFilterPeriode} placeholder="Semua periode" data-testid="lc-filter-periode" />
          </div>

          <div className="space-y-2">
            <LoadingState loading={loadingList} kosong={!loadingList && items.length === 0}
                          pesanKosong="Belum ada data Loss Customer." data-testid="lc-loading">
              {!loadingList && (
                <p className="text-xs text-slate-500">{total} entri</p>
              )}
              {!loadingList && items.map(it => (
                <div key={it.id} data-testid={`lc-row-${it.id}`}
                     className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
                          {it.alasan_label}
                        </span>
                        {it.pelanggan_lama && (
                          <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">
                            Pelanggan lama
                          </span>
                        )}
                        {it.wa_sent_at && (
                          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                            <MessageCircle className="w-3 h-3" /> WA terkirim
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-slate-900 mt-1.5">
                        {it.nama_pelanggan}
                        {it.no_hp && <span className="font-normal text-slate-500"> · {it.no_hp}</span>}
                      </p>
                      <p className="text-sm text-slate-700 mt-0.5">
                        {it.cabang}
                        {it.sales_menangani && <span className="text-xs text-slate-500"> — ditangani {it.sales_menangani}</span>}
                      </p>
                      {it.produk_diminati && (
                        <p className="text-xs text-slate-500 mt-1" data-testid={`lc-produk-${it.id}`}>Mencari: {it.produk_diminati}</p>
                      )}
                      {it.alasan_catatan && <p className="text-xs text-slate-500 mt-1">Catatan: {it.alasan_catatan}</p>}
                      <p className="text-[11px] text-slate-400 mt-1.5">
                        Kunjungan {formatTanggal(it.waktu_kunjungan, { denganJam: true })} WIB · dicatat {it.dicatat_oleh_nama || it.dicatat_oleh || "-"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {bolehUbah && (
                        <button onClick={() => bukaKirimWa(it)} data-testid={`lc-wa-${it.id}`}
                                disabled={!it.no_hp}
                                title={!it.no_hp ? "Tidak ada nomor HP" : "Kirim WhatsApp"}
                                className="h-9 px-3 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-40">
                          <Send className="w-4 h-4" /> WhatsApp
                        </button>
                      )}
                      {bolehUbah && (
                        <button onClick={() => bukaEdit(it)} data-testid={`lc-edit-${it.id}`}
                                className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5">
                          <Pencil className="w-4 h-4" /> Edit
                        </button>
                      )}
                      {bolehHapus && (
                        <button onClick={() => hapus(it)} data-testid={`lc-delete-${it.id}`}
                                className="h-9 px-3 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm font-medium inline-flex items-center gap-1.5">
                          <Trash2 className="w-4 h-4" /> Hapus
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </LoadingState>
          </div>
        </>
      )}

      {/* ===== Tab Rekap ===== */}
      {tab === "rekap" && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <MonthPicker value={rekapPeriode} onChange={setRekapPeriode} placeholder="Semua periode" data-testid="lc-rekap-periode" />
            <input value={rekapCabang} onChange={e => setRekapCabang(e.target.value)} list="lc-branches"
                   data-testid="lc-rekap-cabang" placeholder="Semua cabang"
                   className="h-9 w-48 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          {loadingRekap && <LoadingState loading jumlahKartu={3} data-testid="lc-rekap-loading" />}
          {!loadingRekap && rekap && (
            <>
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Kunjungan Batal</p>
                <p className="font-display text-3xl font-bold text-rose-600 mt-1" data-testid="lc-rekap-total">
                  {rekap.total_kunjungan_batal}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { title: "Per Alasan", rows: rekap.per_alasan, key: "alasan_label", tid: "alasan" },
                  { title: "Per Cabang", rows: rekap.per_cabang, key: "cabang", tid: "cabang" },
                  { title: "Per Sales", rows: rekap.per_sales, key: "sales_menangani", tid: "sales" },
                ].map(panel => (
                  <div key={panel.title} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <p className="text-sm font-semibold text-slate-800 mb-3">{panel.title}</p>
                    <ul className="space-y-2" data-testid={`lc-rekap-${panel.tid}`}>
                      {(panel.rows || []).map((r, i) => (
                        <li key={i} className="text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-700">{r[panel.key]}</span>
                            <span className="text-slate-500">{r.jumlah} ({r.persentase}%)</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
                            <div className="h-full bg-rose-400" style={{ width: `${r.persentase}%` }} />
                          </div>
                        </li>
                      ))}
                      {(!panel.rows || panel.rows.length === 0) && (
                        <li className="text-xs text-slate-400">Tidak ada data.</li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Produk yang paling diincar tapi tidak jadi dibeli (poin 8 owner) —
                  info restock/rencana pembelian, dipisah dari 3 panel di atas
                  karena isinya teks bebas (bukan kategori baku). */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-800">Produk Paling Diincar (Tidak Jadi Dibeli)</p>
                  {rekap.produk_tanpa_isian > 0 && (
                    <span className="text-[10px] text-slate-400">
                      {rekap.produk_tanpa_isian} entri belum diisi produknya
                    </span>
                  )}
                </div>
                <ul className="space-y-2" data-testid="lc-rekap-produk">
                  {(rekap.per_produk_diminati || []).slice(0, 15).map((r, i) => (
                    <li key={i} className="text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-700">{r.produk_diminati}</span>
                        <span className="text-slate-500">{r.jumlah} ({r.persentase}%)</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
                        <div className="h-full bg-amber-400" style={{ width: `${r.persentase}%` }} />
                      </div>
                    </li>
                  ))}
                  {(!rekap.per_produk_diminati || rekap.per_produk_diminati.length === 0) && (
                    <li className="text-xs text-slate-400">Belum ada isian "produk diincar" pada periode/cabang ini.</li>
                  )}
                </ul>
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== Tab Pengaturan ===== */}
      {tab === "pengaturan" && bolehUbah && (
        <div className="space-y-5 max-w-2xl">
          {!pengaturan ? (
            <LoadingState loading jumlahKartu={1} data-testid="lc-pengaturan-loading" />
          ) : (
            <>
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-2">
                <p className="text-sm font-semibold text-slate-800">Template Pesan WhatsApp</p>
                <p className="text-xs text-slate-500">
                  Placeholder yang bisa dipakai: {pengaturan.placeholder_didukung.join(", ")}
                </p>
                <textarea value={pengaturan.template_pesan} rows={4} data-testid="lc-pengaturan-template"
                          placeholder={pengaturan.template_default}
                          onChange={e => setPengaturan(p => ({ ...p, template_pesan: e.target.value }))}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-800">Daftar Alasan Belum Membeli</p>
                  <button onClick={tambahAlasanOpsi} data-testid="lc-pengaturan-tambah-alasan"
                          className="h-8 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-medium inline-flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" /> Tambah
                  </button>
                </div>
                {pengaturan.alasan_opsi.map((a, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input value={a.kode} data-testid={`lc-pengaturan-kode-${idx}`}
                           placeholder="kode_alasan"
                           onChange={e => ubahAlasanOpsi(idx, "kode", e.target.value)}
                           className="h-9 w-40 px-2 rounded-lg border border-slate-200 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    <input value={a.label} data-testid={`lc-pengaturan-label-${idx}`}
                           placeholder="Label yang tampil di form"
                           onChange={e => ubahAlasanOpsi(idx, "label", e.target.value)}
                           className="h-9 flex-1 px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    <button onClick={() => hapusAlasanOpsi(idx)} data-testid={`lc-pengaturan-hapus-${idx}`}
                            className="h-9 w-9 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <p className="text-[11px] text-slate-400">
                  Wajib ada satu baris berkode <span className="font-mono">lainnya</span> — usher menulis catatan bebas kalau tidak ada alasan yang cocok.
                </p>
              </div>
              <button onClick={simpanPengaturan} disabled={pengaturanBusy} data-testid="lc-pengaturan-save"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {pengaturanBusy ? "Menyimpan..." : "Simpan Pengaturan"}
              </button>
            </>
          )}
        </div>
      )}

      {/* ===== Modal: Catat Kunjungan ===== */}
      {form && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={formGuard.requestClose}>
          <div ref={formGuard.contentRef} className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()} data-testid="lc-form-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="font-display font-bold text-base text-slate-900">Catat Kunjungan Loss Customer</p>
              <button onClick={formGuard.requestClose} data-testid="lc-cancel"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <JamOtomatis />
              {renderFormFields(form, setForm, "lc-f")}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
              <button onClick={formGuard.requestClose}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                Batal
              </button>
              <button onClick={simpanForm} disabled={busy} data-testid="lc-save"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {busy ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
            {formGuard.confirming && (
              <UnsavedGuardBar onStay={formGuard.cancelClose} onDiscard={formGuard.confirmDiscard} testid="lc-form-guard-bar" />
            )}
          </div>
        </div>
      )}

      {/* ===== Modal: Edit ===== */}
      {editForm && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={editGuard.requestClose}>
          <div ref={editGuard.contentRef} className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()} data-testid="lc-edit-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="font-display font-bold text-base text-slate-900">Edit Data Loss Customer</p>
              <button onClick={editGuard.requestClose} data-testid="lc-edit-cancel"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              {renderFormFields(editForm, setEditForm, "lc-ef")}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
              <button onClick={editGuard.requestClose}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                Batal
              </button>
              <button onClick={simpanEdit} disabled={editBusy} data-testid="lc-ef-save"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {editBusy ? "Menyimpan..." : "Simpan Perubahan"}
              </button>
            </div>
            {editGuard.confirming && (
              <UnsavedGuardBar onStay={editGuard.cancelClose} onDiscard={editGuard.confirmDiscard} testid="lc-edit-guard-bar" />
            )}
          </div>
        </div>
      )}

      {/* ===== Modal: Kirim WhatsApp ===== */}
      {waTarget && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={() => setWaTarget(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md"
               onClick={e => e.stopPropagation()} data-testid="lc-wa-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="font-display font-bold text-base text-slate-900">Kirim WhatsApp — {waTarget.nama_pelanggan}</p>
              <button onClick={() => setWaTarget(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Info Produk (mengisi placeholder pesan)</label>
                <input value={waProduk} data-testid="lc-wa-produk"
                       placeholder="cth. iPhone 15 Pro Max 256GB, stok baru masuk"
                       onChange={e => setWaProduk(e.target.value)}
                       disabled={!!waPesanCustom.trim()}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-50" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Atau tulis pesan manual (menimpa template)</label>
                <textarea value={waPesanCustom} rows={4} data-testid="lc-wa-pesan-custom"
                          placeholder="Kosongkan untuk memakai template otomatis"
                          onChange={e => setWaPesanCustom(e.target.value)}
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
              <button onClick={() => setWaTarget(null)}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                Batal
              </button>
              <button onClick={kirimWa} disabled={waBusy} data-testid="lc-wa-send"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                <Send className="w-4 h-4" /> {waBusy ? "Mengirim..." : "Kirim"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
